import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlashCard } from '../../lib/gemini';
import { 
  CheckCircle, 
  XCircle, 
  RotateCcw, 
  Shuffle, 
  Trophy, 
  Clock,
  Target,
  Users,
  AlertCircle,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Zap,
  Star,
  Heart,
  Sparkles,
  Edit3,
  BookOpen,
  Lightbulb
} from 'lucide-react';

interface FillInTheBlankGameProps {
  flashcards: FlashCard[];
  onComplete?: (score: number, timeSpent: number) => void;
}

interface FillInTheBlankQuestion {
  id: string;
  question: string;
  correctAnswer: string;
  userAnswer: string;
  isCorrect: boolean;
  explanation: string;
  hint: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface GameStats {
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  hintsUsed: number;
  averageTimePerQuestion: number;
  streak: number;
  bestStreak: number;
}

export const FillInTheBlankGame: React.FC<FillInTheBlankGameProps> = ({ flashcards, onComplete }) => {
  const [questions, setQuestions] = useState<FillInTheBlankQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [gameState, setGameState] = useState<'playing' | 'completed' | 'review'>('playing');
  const [score, setScore] = useState(0);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [timeSpent, setTimeSpent] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string; icon?: React.ReactNode } | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [gameStats, setGameStats] = useState<GameStats>({
    totalQuestions: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    hintsUsed: 0,
    averageTimePerQuestion: 0,
    streak: 0,
    bestStreak: 0
  });

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  // Initialize game
  useEffect(() => {
    initializeGame();
  }, [flashcards]);

  // Timer
  useEffect(() => {
    if (gameState === 'playing') {
      const interval = setInterval(() => {
        setTimeSpent(Date.now() - startTime);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameState, startTime]);

  // Clear feedback after 3 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const initializeGame = useCallback(() => {
    if (!flashcards.length) return;

    // Generate fill-in-the-blank questions from flashcards
    const generatedQuestions: FillInTheBlankQuestion[] = flashcards.map((card, index) => {
      const { question, correctAnswer, hint } = generateFillInTheBlankQuestion(card);
      
      return {
        id: `question-${index}`,
        question,
        correctAnswer,
        userAnswer: '',
        isCorrect: false,
        explanation: card.definition,
        hint,
        difficulty: Math.random() > 0.7 ? 'hard' : Math.random() > 0.4 ? 'medium' : 'easy'
      };
    });

    // Shuffle questions
    const shuffledQuestions = shuffleArray([...generatedQuestions]);
    setQuestions(shuffledQuestions);
    setCurrentQuestionIndex(0);
    setGameState('playing');
    setStartTime(Date.now());
    setTimeSpent(0);
    setScore(0);
    setShowHint(false);
    setFeedback(null);
    setShowResults(false);
    setGameStats({
      totalQuestions: shuffledQuestions.length,
      correctAnswers: 0,
      incorrectAnswers: 0,
      hintsUsed: 0,
      averageTimePerQuestion: 0,
      streak: 0,
      bestStreak: 0
    });
  }, [flashcards]);

  const shuffleArray = (array: any[]) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const generateFillInTheBlankQuestion = (flashcard: FlashCard) => {
    const { term, definition } = flashcard;
    
    // Create different types of questions
    const questionTypes = [
      // Type 1: Fill in the term in a definition
      () => ({
        question: definition.replace(new RegExp(term, 'gi'), '_____'),
        correctAnswer: term,
        hint: `The missing word is a key concept related to: ${definition.substring(0, 50)}...`
      }),
      
      // Type 2: Fill in a definition with the term
      () => ({
        question: `What is the definition of "${term}"?`,
        correctAnswer: definition,
        hint: `Think about what ${term} means in this context`
      }),
      
      // Type 3: Complete a sentence about the term
      () => ({
        question: `Complete this sentence: "${term} is a _____ that ${definition.substring(definition.indexOf(' ') + 1)}"`,
        correctAnswer: definition.split(' ')[0] || 'concept',
        hint: `This word describes what ${term} is`
      })
    ];

    const selectedType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    return selectedType();
  };

  const handleAnswerChange = (answer: string) => {
    if (gameState !== 'playing') return;
    
    setQuestions(prev => prev.map((q, index) => 
      index === currentQuestionIndex ? { ...q, userAnswer: answer } : q
    ));
  };

  const handleSubmitAnswer = () => {
    if (!currentQuestion || gameState !== 'playing') return;

    const isCorrect = currentQuestion.userAnswer.toLowerCase().trim() === 
                     currentQuestion.correctAnswer.toLowerCase().trim();
    
    const updatedQuestions = questions.map((q, index) => 
      index === currentQuestionIndex ? { ...q, isCorrect } : q
    );
    setQuestions(updatedQuestions);

    // Update stats
    const newStats = { ...gameStats };
    if (isCorrect) {
      newStats.correctAnswers++;
      newStats.streak++;
      if (newStats.streak > newStats.bestStreak) {
        newStats.bestStreak = newStats.streak;
      }
      setFeedback({
        type: 'success',
        message: 'Correct! Well done!',
        icon: <CheckCircle className="h-5 w-5" />
      });
    } else {
      newStats.incorrectAnswers++;
      newStats.streak = 0;
      setFeedback({
        type: 'error',
        message: `Incorrect. The correct answer is: ${currentQuestion.correctAnswer}`,
        icon: <XCircle className="h-5 w-5" />
      });
    }
    setGameStats(newStats);

    // Move to next question or complete game
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setShowHint(false);
        setFeedback(null);
      } else {
        completeGame();
      }
    }, 2000);
  };

  const handleShowHint = () => {
    setShowHint(true);
    setGameStats(prev => ({ ...prev, hintsUsed: prev.hintsUsed + 1 }));
  };

  const completeGame = () => {
    const finalScore = Math.round((gameStats.correctAnswers / questions.length) * 100);
    const avgTime = timeSpent / questions.length;
    
    setGameStats(prev => ({ ...prev, averageTimePerQuestion: avgTime }));
    setScore(finalScore);
    setGameState('completed');
    
    if (onComplete) {
      onComplete(finalScore, timeSpent);
    }
  };

  const resetGame = () => {
    initializeGame();
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success-600';
    if (score >= 60) return 'text-warning-600';
    return 'text-error-600';
  };

  const getScoreMessage = (score: number) => {
    if (score >= 90) return 'Excellent work! 🎉';
    if (score >= 80) return 'Great job! 👏';
    if (score >= 70) return 'Good effort! 👍';
    if (score >= 60) return 'Keep practicing! 💪';
    return 'Review the material and try again! 📚';
  };

  if (gameState === 'completed') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto space-y-6"
      >
        {/* Results Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="mb-4">
            <Trophy className="h-12 w-12 text-primary-600 mx-auto mb-2" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Game Complete!
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {gameStats.correctAnswers} out of {questions.length} correct
            </p>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {getScoreMessage(score)}
            </p>
            
            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Time Spent</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatTime(timeSpent)}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Best Streak</p>
                <p className="font-medium text-gray-900 dark:text-white">{gameStats.bestStreak}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Hints Used</p>
                <p className="font-medium text-gray-900 dark:text-white">{gameStats.hintsUsed}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Avg Time/Question</p>
                <p className="font-medium text-gray-900 dark:text-white">{Math.round(gameStats.averageTimePerQuestion / 1000)}s</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={resetGame}
            className="mt-6 flex items-center space-x-2 mx-auto px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Play Again</span>
          </button>
        </div>

        {/* Detailed Results */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Review Your Answers
          </h3>
          {questions.map((question, index) => (
            <div
              key={question.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-start space-x-3">
                {question.isCorrect ? (
                  <CheckCircle className="h-5 w-5 text-success-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-error-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">
                    {question.question}
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                      Your answer: <span className={question.isCorrect ? 'text-success-600' : 'text-error-600'}>
                        {question.userAnswer || 'No answer'}
                      </span>
                    </p>
                    {!question.isCorrect && (
                      <p className="text-gray-600 dark:text-gray-400">
                        Correct answer: <span className="text-success-600">{question.correctAnswer}</span>
                      </p>
                    )}
                    <p className="text-gray-500 dark:text-gray-400 italic">
                      {question.explanation}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  if (!questions.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No questions available</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Progress and Timer */}
      <div className="flex justify-between items-center">
        <div className="flex-1 mr-4">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <motion.div
              className="bg-primary-600 h-2 rounded-full"
              style={{ width: `${progress}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Question {currentQuestionIndex + 1} of {questions.length}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {formatTime(timeSpent)}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">Correct</p>
          <p className="text-lg font-bold text-success-600">{gameStats.correctAnswers}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">Streak</p>
          <p className="text-lg font-bold text-primary-600">{gameStats.streak}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">Hints</p>
          <p className="text-lg font-bold text-warning-600">{gameStats.hintsUsed}</p>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <BookOpen className="h-5 w-5 text-primary-600" />
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Fill in the blank
            </span>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            currentQuestion?.difficulty === 'easy' ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-400' :
            currentQuestion?.difficulty === 'medium' ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400' :
            'bg-error-100 text-error-700 dark:bg-error-900/20 dark:text-error-400'
          }`}>
            {currentQuestion?.difficulty}
          </div>
        </div>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          {currentQuestion?.question}
        </h2>

        {/* Answer Input */}
        <div className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={currentQuestion?.userAnswer || ''}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              disabled={gameState !== 'playing'}
            />
          </div>

          {/* Hint */}
          {!showHint && (
            <button
              onClick={handleShowHint}
              className="flex items-center space-x-2 text-warning-600 dark:text-warning-400 hover:text-warning-700 dark:hover:text-warning-300 transition-colors"
            >
              <Lightbulb className="h-4 w-4" />
              <span className="text-sm">Show hint</span>
            </button>
          )}

          {showHint && currentQuestion && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800"
            >
              <div className="flex items-center space-x-2 mb-2">
                <Lightbulb className="h-4 w-4 text-warning-600" />
                <span className="text-sm font-medium text-warning-800 dark:text-warning-200">Hint</span>
              </div>
              <p className="text-sm text-warning-700 dark:text-warning-300">
                {currentQuestion.hint}
              </p>
            </motion.div>
          )}
        </div>

        {/* Feedback */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`mt-4 p-4 rounded-lg flex items-center space-x-3 ${
                feedback.type === 'success' 
                  ? 'bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800' 
                  : feedback.type === 'error'
                  ? 'bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800'
                  : 'bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800'
              }`}
            >
              {feedback.icon}
              <span className={`text-sm font-medium ${
                feedback.type === 'success' 
                  ? 'text-success-800 dark:text-success-200' 
                  : feedback.type === 'error'
                  ? 'text-error-800 dark:text-error-200'
                  : 'text-info-800 dark:text-info-200'
              }`}>
                {feedback.message}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSubmitAnswer}
            disabled={!currentQuestion?.userAnswer?.trim() || gameState !== 'playing'}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <span>Submit Answer</span>
            <CheckCircle className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}; 